/**
 * Relay global: telemetría solar (agregado "planta", inversores activos)
 * para el listado de dispositivos y el dashboard sin abrir Energía solar.
 * Misma ruta WebSocket y mismos campos agregados que usa SolarEnergy para el tab Flujo.
 */

import io from 'socket.io-client';
import { getServerUrl } from '../config';
import { getToken } from './auth';
import api from './api';
import { publishSolarBatteryPowerW, publishSolarPvTotalPowerW } from './solarFlowLiveTelemetry';

type InverterRow = { id: number; activo?: boolean };

let refCount = 0;
let socket: ReturnType<typeof io> | null = null;
let invertersCache: InverterRow[] = [];
let throttleTimer: ReturnType<typeof setTimeout> | null = null;
let lastThrottleApply = 0;
const THROTTLE_MS = 500;
let pendingPayload: { inversores: any[] } | null = null;

function isValidSolarPayload(data: unknown): data is { inversores: any[] } {
  if (!data || typeof data !== 'object') return false;
  const inv = (data as any).inversores;
  if (!Array.isArray(inv) || inv.length === 0) return false;
  return inv.some((row: any) => row && row.inversor_id != null && row.inversor_id !== undefined);
}

function sumForActiveInverters(
  inversoresData: any[],
  activeIds: number[],
  key: string
): number | null {
  let total = 0;
  let hasAny = false;
  for (const id of activeIds) {
    const row = inversoresData.find((r: any) => r && r.inversor_id === id);
    if (!row) continue;
    const val = row[key];
    if (typeof val === 'number' && !Number.isNaN(val)) {
      total += val;
      hasAny = true;
    }
  }
  return hasAny ? total : null;
}

function processPayload(data: { inversores: any[] }): void {
  const activeIds = invertersCache.filter((i) => i.activo === true).map((i) => i.id);
  if (activeIds.length === 0) {
    return;
  }
  const battery = sumForActiveInverters(data.inversores, activeIds, 'battery_power');
  const pv = sumForActiveInverters(data.inversores, activeIds, 'pv_total_power');
  if (battery !== null) {
    publishSolarBatteryPowerW(battery);
  }
  if (pv !== null) {
    publishSolarPvTotalPowerW(pv);
  }
}

function applyThrottled(data: { inversores: any[] }): void {
  const now = Date.now();
  if (now - lastThrottleApply >= THROTTLE_MS) {
    lastThrottleApply = now;
    processPayload(data);
    pendingPayload = null;
    return;
  }
  pendingPayload = data;
  if (throttleTimer) {
    return;
  }
  const delay = Math.max(0, THROTTLE_MS - (now - lastThrottleApply));
  throttleTimer = setTimeout(() => {
    throttleTimer = null;
    lastThrottleApply = Date.now();
    if (pendingPayload && isValidSolarPayload(pendingPayload)) {
      processPayload(pendingPayload);
    }
    pendingPayload = null;
  }, delay);
}

async function loadInverters(): Promise<void> {
  try {
    const res = await api.get<InverterRow[]>('/solar/inversores');
    invertersCache = Array.isArray(res.data) ? res.data : [];
  } catch {
    invertersCache = [];
  }
}

async function pollHttpOnce(): Promise<void> {
  // Solo /quick: el fallback a /current saturaba la BD (consulta pesada).
  // El WebSocket solar_update cubre el resto en vivo.
  try {
    const quick = await api.get('/solar/current/quick');
    if (isValidSolarPayload(quick.data)) {
      processPayload(quick.data);
    }
  } catch {
    /* sin datos; WS puede recuperar */
  }
}

function disconnectSocket(): void {
  if (throttleTimer) {
    clearTimeout(throttleTimer);
    throttleTimer = null;
  }
  pendingPayload = null;
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
}

function connectSocket(): void {
  const token = getToken();
  if (!token) {
    return;
  }
  disconnectSocket();

  const serverUrl = getServerUrl();
  const newSocket = io(serverUrl, {
    path: '/socket.io-solar',
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 1000,
    timeout: 10000,
  });

  newSocket.on('connect', () => {
    newSocket.emit('authenticate', { token });
  });

  newSocket.on('solar_update', (data: unknown) => {
    if (!isValidSolarPayload(data)) {
      return;
    }
    applyThrottled(data);
  });

  newSocket.on('authenticated', () => {
    void pollHttpOnce();
  });

  socket = newSocket;
}

async function bootstrap(): Promise<void> {
  await loadInverters();
  if (refCount <= 0) {
    return;
  }
  await pollHttpOnce();
  if (refCount <= 0) {
    return;
  }
  connectSocket();
  if (refCount <= 0) {
    disconnectSocket();
  }
}

function tearDown(): void {
  disconnectSocket();
  invertersCache = [];
}

/**
 * Inicia el relay si es el primer suscriptor. Devuelve cleanup.
 * Idempotente con refCount para React StrictMode / re-login.
 */
export function startSolarFlowMetricsRelay(): () => void {
  refCount += 1;
  if (refCount === 1) {
    void bootstrap();
  }
  return () => {
    refCount -= 1;
    if (refCount <= 0) {
      tearDown();
    }
  };
}
